// ============================================================
// faceUtils.js — FINAL ENTERPRISE VERSION (FAST + ACCURATE)
// ============================================================

import * as faceapi from "face-api.js";

// ────────────────────────────────────────────────────────────
// 🔥 MODEL LOADER (CACHED)
// ────────────────────────────────────────────────────────────
let modelsLoaded = false;

export async function loadModels() {
  if (modelsLoaded) return;

  const MODEL_URL = "/models";

  await Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
    faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
  ]);

  modelsLoaded = true;
  console.log("✅ Models Loaded");
}

// ────────────────────────────────────────────────────────────
// 🎯 SAFE FACE DETECTION (CRASH PROOF)
// ────────────────────────────────────────────────────────────
export async function detectFace(video) {
  try {
    if (
      !video ||
      video.readyState !== 4 ||
      video.videoWidth === 0 ||
      video.videoHeight === 0
    ) return null;

    const detection = await faceapi
      .detectSingleFace(
        video,
        new faceapi.TinyFaceDetectorOptions({
          inputSize: 160,
          scoreThreshold: 0.5,
        })
      )
      .withFaceLandmarks()
      .withFaceDescriptor();

    // 🔥 HARD VALIDATION (prevents Box crash)
    if (
      !detection ||
      !detection.detection ||
      !detection.detection.box ||
      detection.detection.box.x == null ||
      detection.detection.box.y == null ||
      detection.detection.box.width == null ||
      detection.detection.box.height == null
    ) return null;

    return detection;

  } catch {
    return null;
  }
}

// ────────────────────────────────────────────────────────────
// 🎯 FACE QUALITY CHECK
// ────────────────────────────────────────────────────────────
export function checkFaceQuality(detection) {
  if (!detection) return { ok: false, msg: "No face detected" };

  const box = detection.detection.box;
  const score = detection.detection.score;

  if (score < 0.6) return { ok: false, msg: "Low confidence" };
  if (box.width < 90) return { ok: false, msg: "Move closer" };
  if (box.width > 400) return { ok: false, msg: "Too close" };

  return { ok: true, msg: "Good" };
}

// ────────────────────────────────────────────────────────────
// 🧠 DESCRIPTOR NORMALIZATION
// ────────────────────────────────────────────────────────────
export function normalizeDescriptor(desc) {
  const norm = Math.sqrt(desc.reduce((sum, v) => sum + v * v, 0));
  return desc.map(v => v / norm);
}

// ────────────────────────────────────────────────────────────
// 📦 GET DESCRIPTOR ARRAY
// ────────────────────────────────────────────────────────────
export function getDescriptorArray(detection) {
  if (!detection || !detection.descriptor) return null;
  return normalizeDescriptor(Array.from(detection.descriptor));
}

// ────────────────────────────────────────────────────────────
// 🧠 AVERAGE DESCRIPTORS (KEY FOR ACCURACY)
// ────────────────────────────────────────────────────────────
function averageDescriptors(list) {
  return list[0].map((_, i) =>
    list.reduce((sum, d) => sum + d[i], 0) / list.length
  );
}

// ────────────────────────────────────────────────────────────
// 🔧 GET DESCRIPTORS FROM EMPLOYEE (Supports both formats)
// ────────────────────────────────────────────────────────────
function getEmployeeDescriptors(emp) {
  // Support both 'descriptor' (single) and 'faceDescriptors' (array)
  if (emp.faceDescriptors && Array.isArray(emp.faceDescriptors) && emp.faceDescriptors.length > 0) {
    return emp.faceDescriptors;
  } else if (emp.descriptor && Array.isArray(emp.descriptor) && emp.descriptor.length > 0) {
    return [emp.descriptor];
  }
  return null;
}

// ────────────────────────────────────────────────────────────
// 🚀 ENTERPRISE MATCHING ENGINE (FIXED)
// ────────────────────────────────────────────────────────────
export function matchFace(inputDescriptor, employees, threshold = 0.60) {
  if (!inputDescriptor) return null;

  let bestMatch = null;
  let bestDistance = Infinity;

  const input = new Float32Array(inputDescriptor);

  console.log(`🔍 Matching against ${employees.length} employees`);

  for (const emp of employees) {
    const descriptors = getEmployeeDescriptors(emp);
    
    if (!descriptors) {
      console.log(`⚠️ Skipping ${emp.name || emp.id} - No valid descriptor found`);
      continue;
    }

    try {
      // Use average if multiple descriptors, otherwise use single
      const avg = descriptors.length === 1 
        ? descriptors[0] 
        : averageDescriptors(descriptors);
      
      const normalizedAvg = normalizeDescriptor(avg);

      const dist = faceapi.euclideanDistance(
        input,
        new Float32Array(normalizedAvg)
      );

      const confidence = Math.round((1 - dist) * 100);
      console.log(`  ${emp.name || emp.id}: distance=${dist.toFixed(3)}, confidence=${confidence}%`);

      if (dist < bestDistance) {
        bestDistance = dist;
        bestMatch = emp;
      }

    } catch (err) {
      console.warn(`  ❌ Error matching ${emp.name || emp.id}:`, err);
    }
  }

  // No valid match found
  if (!bestMatch || bestDistance > threshold) {
    console.log(`❌ No match found (best distance: ${bestDistance.toFixed(3)}, threshold: ${threshold})`);
    return null;
  }

  const finalConfidence = Math.round((1 - bestDistance) * 100);
  console.log(`✅ Best match: ${bestMatch.name || bestMatch.id} (${finalConfidence}%)`);

  return {
    user: bestMatch,
    confidence: finalConfidence,
    distance: bestDistance,
  };
}

// ────────────────────────────────────────────────────────────
// 🔁 DUPLICATE CHECK (FIXED)
// ────────────────────────────────────────────────────────────
export function isDuplicate(newDescriptor, employees, threshold = 0.45) {
  if (!newDescriptor) return null;

  const input = new Float32Array(newDescriptor);

  for (const emp of employees) {
    const descriptors = getEmployeeDescriptors(emp);
    if (!descriptors) continue;

    const avg = descriptors.length === 1 
      ? descriptors[0] 
      : averageDescriptors(descriptors);

    try {
      const dist = faceapi.euclideanDistance(input, new Float32Array(avg));

      if (dist < threshold) return emp;
    } catch {}
  }

  return null;
}

// ────────────────────────────────────────────────────────────
// 👁️ BLINK DETECTION (ANTI-SPOOF)
// ────────────────────────────────────────────────────────────
export function detectBlink(landmarks) {
  try {
    const calcEAR = (eye) => {
      const A = Math.hypot(eye[1].x - eye[5].x, eye[1].y - eye[5].y);
      const B = Math.hypot(eye[2].x - eye[4].x, eye[2].y - eye[4].y);
      const C = Math.hypot(eye[0].x - eye[3].x, eye[0].y - eye[3].y);
      return (A + B) / (2 * C);
    };

    const ear =
      (calcEAR(landmarks.getLeftEye()) +
        calcEAR(landmarks.getRightEye())) / 2;

    return ear < 0.22;

  } catch {
    return false;
  }
}

// ────────────────────────────────────────────────────────────
// 👁️ BLINK TRACKER (STATEFUL)
// ────────────────────────────────────────────────────────────
export class BlinkTracker {
  constructor() {
    this.closedFrames = 0;
    this.wasOpen = true;
    this.verified = false;
  }

  update(landmarks) {
    if (this.verified) return true;

    const isClosed = detectBlink(landmarks);

    if (this.wasOpen && isClosed) {
      this.closedFrames++;
      this.wasOpen = false;
    } else if (!isClosed) {
      this.wasOpen = true;
    }

    if (this.closedFrames >= 1) {
      this.verified = true;
      return true;
    }

    return false;
  }

  reset() {
    this.closedFrames = 0;
    this.wasOpen = true;
    this.verified = false;
  }
}